//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import CheckCircle from 'assets/check-circle.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { ReactComponent as InformationCircle } from 'assets/information-circle.svg';
import personCircle from 'assets/person-circle.svg';
import { AxiosError } from 'axios';
import { useAuth } from 'contexts/AuthProvider';
import { useToast } from 'contexts/ToastProvider';
import { CbtUserUpdateDto } from 'dto/cbt-user-update.dto';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getRoleText } from 'utils/helper';
import { getItem } from 'utils/localStorageHelper';
import Validator from 'utils/validator';
import { Role } from '..';
import AccountType from './AccountType';
import CurrentPasswordGroup from './CurrentPasswordGroup';

export interface UserUpdateFormData extends CbtUserUpdateDto {
  email?: string;
  confirmPassword?: string;
}

const MyAccount = ({ setShowDialog }: { setShowDialog: Dispatch<SetStateAction<boolean>> }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { logout, user, putCbtUserUpdate, getUser } = useAuth();
  const validator = Validator();
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [usernameEditable, setUserNameEditable] = useState<boolean>(false);
  const [passwordEditable, setPasswordEditable] = useState<boolean>(false);
  const methods = useForm<UserUpdateFormData>();

  const getUserInfo = () => {
    setIsOwner(user.role === Role.Owner);
    const userInfo: UserUpdateFormData = {
      email: user.email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      name: user.name,
    };
    methods.reset(userInfo);
  };

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setShowDialog(methods.formState.isDirty && !methods.formState.isSubmitSuccessful);
  }, [methods.formState.isDirty, methods.formState.isSubmitSuccessful]);

  const resetForm = () => {
    methods.reset();
    setPasswordEditable(false);
    setUserNameEditable(false);
  };

  const onSubmit = (data: UserUpdateFormData) => {
    if (data.name === '') {
      data.name = user.email;
    }

    /* NOTE: PUT /api/users/cbtUserUpdate/{id} requst body properties都必要，
             當僅修改 User name，需要輸入 currentPassword 給 API 做驗證，故 newPassword 直接填入 currentPassword
     */
    if (data.newPassword === '') {
      data.newPassword = data.currentPassword;
    }

    delete data.email;
    delete data.confirmPassword;

    putCbtUserUpdate(data)
      .then(() => {
        resetForm();
        getUser();
        addToast({
          title: t('toast.updatedSuccessfully'),
          message: '',
          color: 'bg-toast-success-50',
          icon: CheckCircle,
          text: 'text-toast-success-800',
          svg: 'toastIcon-success',
        });
        if (getItem('isFromCloud') && user.cloud) {
          logout();
        }
      })
      .catch((error: AxiosError<{ status: number; statusCode: string; message: string }>) => {
        let message: string = error.response?.data.message ?? '';
        addToast({
          title: t('toast.updatedFailed'),
          message,
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };

  return (
    <div className="card-body setting">
      <FormProvider {...methods}>
        <form className="flex flex-col" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center border-b p-6">
            <div className="flex items-center space-x-3">
              <div className="avatar mask mask-squircle w-12 h-12">
                <img src={personCircle} alt="Avatar Tailwind CSS Component" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm opacity-50">
                  <span className="capitalize">{getRoleText(user.role)}</span>
                </span>
                <span className="font-bold">{user.email}</span>
              </div>
            </div>
            <button type="button" className="btn btn-primary" onClick={logout} data-testid="my-account-logout-btn">
              {t('modal.btnLogOut')}
            </button>
          </div>
          <div className="label px-6 border-b border-slate-300">
            <label className="label-text w-64">{t('profile.formUserName')}</label>
            <div className="flex flex-col space-y-1 flex-1">
              {usernameEditable ? (
                <>
                  <input
                    type="text"
                    placeholder={user.email}
                    className="input max-w-xs"
                    {...methods.register('name', {
                      required: validator.required,
                      minLength: validator.minLength(1, 32),
                      maxLength: validator.maxLength(1, 32),
                      pattern: validator.namePassword,
                      validate: {
                        checkSpace: (v) => !v || validator.checkNameBeginEndWithSpace(v),
                      },
                    })}
                    data-testid="my-account-input-for-name-name"
                  />
                  {methods.formState.errors.name && <span className="span-error px-1">{methods.formState.errors.name.message}</span>}
                </>
              ) : (
                <p>{user.name}</p>
              )}
            </div>
            {!usernameEditable && (
              <button
                type="button"
                className="btn btn-link text-[#489EE5]"
                onClick={() => setUserNameEditable(true)}
                data-testid="my-account-rename-btn"
              >
                {t('modal.btnRename')}
              </button>
            )}
          </div>
          <div className="label px-6 border-b border-slate-300">
            <label className="flex items-center label-text w-64">
              {t('profile.formEmail')}
              <div className="ml-1 tooltip tooltip-right text-left" data-tip={t('profile.formEmailTip')}>
                <InformationCircle className="w-4 h-4 text-gray-400" />
              </div>
            </label>
            <p>{user.email}</p>
          </div>
          <CurrentPasswordGroup passwordEditable={passwordEditable} usernameEditable={usernameEditable} setPasswordEditable={setPasswordEditable} />
          <AccountType cloud={user.cloud} isOwner={isOwner} getUserInfo={getUserInfo} />
          {(methods.formState.isDirty || passwordEditable || usernameEditable) && (
            <div className="flex space-x-2 justify-end mt-5">
              <button type="button" className="btn btn-outline" onClick={resetForm} data-testid="cancel-my-account-btn">
                {t('modal.btnCancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={!methods.formState.isDirty} data-testid="apply-my-account-btn">
                {t('modal.btnApply')}
              </button>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default MyAccount;
